import React, { useEffect, useRef, useState, useCallback } from 'react'
import { X } from 'lucide-react'
import './ImageCropperModal.css'

/**
 * Fullscreen image cropper modal. The user can pan/pinch/wheel-zoom inside
 * a square viewport with a circular overlay; "Save" produces a 400x400
 * JPEG Blob via Canvas and hands it back to the caller through onCropDone.
 *
 * Mirrors the iOS ImageCropperView and the Android ImageCropperScreen so
 * profile-photo cropping feels the same on every platform.
 */
function ImageCropperModal({ imageSrc, onCancel, onCropDone }) {
  const viewportRef = useRef(null)
  const imgRef = useRef(null)

  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 })
  const [imgDisplaySize, setImgDisplaySize] = useState({ w: 0, h: 0 })
  const [viewportSize, setViewportSize] = useState(0)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [busy, setBusy] = useState(false)

  // Pointer-tracking for pan + pinch.
  const pointersRef = useRef(new Map())
  const dragStartRef = useRef(null)
  const pinchStartRef = useRef(null)

  // Square viewport sized to fit the screen (with chrome margins).
  useEffect(() => {
    const recalc = () => {
      const margin = 80 // top bar + bottom controls
      const side = Math.min(
        window.innerWidth - 32,
        window.innerHeight - margin * 2
      )
      setViewportSize(Math.max(240, Math.floor(side)))
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [])

  const handleImgLoad = (e) => {
    const img = e.target
    setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
    setScale(1)
    setOffset({ x: 0, y: 0 })
  }

  // Recompute the image's "fill" display size whenever the natural size or
  // viewport size changes. Shorter source side fills the viewport (object-
  // fit: cover semantics), longer side overflows and is clipped — the user
  // can drag to reveal it.
  useEffect(() => {
    if (!imgNaturalSize.w || !viewportSize) return
    const aspect = imgNaturalSize.w / imgNaturalSize.h
    if (aspect >= 1) {
      setImgDisplaySize({ w: viewportSize * aspect, h: viewportSize })
    } else {
      setImgDisplaySize({ w: viewportSize, h: viewportSize / aspect })
    }
  }, [imgNaturalSize, viewportSize])

  // ----- Gestures -----

  const onPointerDown = (e) => {
    if (!viewportRef.current) return
    viewportRef.current.setPointerCapture(e.pointerId)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointersRef.current.size === 1) {
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        startOffset: { ...offset },
      }
    } else if (pointersRef.current.size === 2) {
      // Begin pinch
      const pts = Array.from(pointersRef.current.values())
      const dx = pts[0].x - pts[1].x
      const dy = pts[0].y - pts[1].y
      pinchStartRef.current = {
        distance: Math.hypot(dx, dy),
        startScale: scale,
      }
      dragStartRef.current = null
    }
  }

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointersRef.current.size === 2 && pinchStartRef.current) {
      const pts = Array.from(pointersRef.current.values())
      const dx = pts[0].x - pts[1].x
      const dy = pts[0].y - pts[1].y
      const dist = Math.hypot(dx, dy)
      const next = pinchStartRef.current.startScale * (dist / pinchStartRef.current.distance)
      setScale(Math.max(1, Math.min(5, next)))
    } else if (pointersRef.current.size === 1 && dragStartRef.current) {
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      setOffset({
        x: dragStartRef.current.startOffset.x + dx,
        y: dragStartRef.current.startOffset.y + dy,
      })
    }
  }

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchStartRef.current = null
    if (pointersRef.current.size === 0) dragStartRef.current = null
  }

  const onWheel = (e) => {
    // Pinch on a trackpad arrives as wheel + ctrlKey; standard wheel = zoom too.
    e.preventDefault()
    const delta = -e.deltaY
    const factor = Math.exp(delta * 0.0015)
    setScale((s) => Math.max(1, Math.min(5, s * factor)))
  }

  // Wheel needs to be a non-passive listener to allow preventDefault.
  useEffect(() => {
    const node = viewportRef.current
    if (!node) return
    const handler = (e) => onWheel(e)
    node.addEventListener('wheel', handler, { passive: false })
    return () => node.removeEventListener('wheel', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ----- Save -----

  const handleSave = useCallback(async () => {
    if (busy || !imgNaturalSize.w || !viewportSize) return
    setBusy(true)
    try {
      // ContentScale.Crop math: scale source so its SHORTER side fills the
      // viewport square, longer side overflows. Then apply the user's pan
      // and zoom on top of that base.
      const baseScale = viewportSize / Math.min(imgNaturalSize.w, imgNaturalSize.h)
      const effective = baseScale * scale

      // Output is 400x400. We render the source bitmap into the output
      // canvas using the same transform stack the on-screen viewport uses,
      // scaled down from `viewportSize` to 400.
      const OUT = 400
      const outScale = OUT / viewportSize
      const canvas = document.createElement('canvas')
      canvas.width = OUT
      canvas.height = OUT
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Translate to viewport-equivalent center, apply offset (in screen
      // pixels), apply effective scale, then draw image centered on origin.
      ctx.translate(OUT / 2 + offset.x * outScale, OUT / 2 + offset.y * outScale)
      ctx.scale(effective * outScale, effective * outScale)
      ctx.drawImage(
        imgRef.current,
        -imgNaturalSize.w / 2,
        -imgNaturalSize.h / 2,
        imgNaturalSize.w,
        imgNaturalSize.h
      )

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
      )
      if (!blob) throw new Error('Could not create cropped image.')
      onCropDone(blob)
    } catch (err) {
      console.error('Crop failed:', err)
      setBusy(false)
    }
  }, [busy, imgNaturalSize, viewportSize, scale, offset, onCropDone])

  return (
    <div className="image-cropper-modal" role="dialog" aria-label="Crop photo">
      <div className="image-cropper-topbar">
        <button className="image-cropper-icon-btn" onClick={onCancel} aria-label="Cancel">
          <X size={22} />
        </button>
        <div className="image-cropper-title">Move and Scale</div>
        <button className="image-cropper-save-btn" onClick={handleSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="image-cropper-stage">
        <div
          ref={viewportRef}
          className="image-cropper-viewport"
          style={{ width: viewportSize, height: viewportSize }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {imageSrc && (
            <img
              ref={imgRef}
              src={imageSrc}
              alt=""
              draggable={false}
              onLoad={handleImgLoad}
              className="image-cropper-img"
              style={{
                width: imgDisplaySize.w || 'auto',
                height: imgDisplaySize.h || 'auto',
                transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              }}
            />
          )}
          {/* Single SVG overlay: dim outside the circle + dashed white
              boundary, both sharing r="46%" so they're pixel-perfectly
              aligned (no "circle inside a circle" artifact). */}
          <svg
            className="image-cropper-overlay-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <mask id="cropMask">
                <rect width="100" height="100" fill="white" />
                <circle cx="50" cy="50" r="46" fill="black" />
              </mask>
            </defs>
            <rect width="100" height="100" fill="rgba(0, 0, 0, 0.55)" mask="url(#cropMask)" />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="rgba(255, 255, 255, 0.9)"
              strokeWidth="0.4"
              strokeDasharray="1.6 1.2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      </div>

      <div className="image-cropper-hint">Drag to move • Pinch or scroll to zoom</div>
    </div>
  )
}

export default ImageCropperModal
